import { useDispatch, useSelector } from 'react-redux';
import { hasElements, NO_ELEMENT, rm } from 'utils';
import { selectAddonServices, selectAddonServicesZipCode } from './selector';
import styles from './styles.scss';
import { useToggle } from 'hooks';
import { ZipCodeInput } from 'components/Shared/ZipCodeInput';
import { useEffect, useReducer, useRef } from 'react';
import { initialState, reducer } from './reducer';
import {
  getFormattedZipCode,
  isValidZipcode
} from 'components/Shared/ZipCodeInput/themes/BGH/validation';
import { LoadingStatus } from 'models/LoadingStatus';
import { getAllUniqueIds, getPPIFileName, getPPIFilePath, postZipCode } from './utils';
import { Header } from './components/header';
import {
  deselectAddonService,
  deselectAllAddonServices,
  selectAddonService,
  setAddonServiceZipCode
} from '../actions';
import { showItemSection } from './components/itemSection/itemSection';
import { IconPdf } from 'components/Shared/Icon';

export const AddonServices = () => {
  const addonServices = useSelector(selectAddonServices);
  const zipCode = useSelector(selectAddonServicesZipCode);
  const [isOpen, toggleOpen] = useToggle(true);
  const [{ loadingStatus, validInstallations }, dispatch] = useReducer(reducer, initialState);
  const hasSelection = useRef(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (hasSelection.current) {
      reduxDispatch(deselectAllAddonServices());
      hasSelection.current = false;
    }
    if (!isValidZipcode(getFormattedZipCode(zipCode))) {
      return;
    }

    postZipCode(zipCode, getAllUniqueIds(addonServices), dispatch, reduxDispatch);

    // Excluding addonServices to avoid re-render loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxDispatch, zipCode]);

  if (!hasElements(addonServices)) {
    return NO_ELEMENT;
  }

  const setZipCode = (event: React.ChangeEvent<HTMLInputElement>) => {
    reduxDispatch(setAddonServiceZipCode(event.currentTarget.value));
  };

  const clearZipCode = () => {
    reduxDispatch(setAddonServiceZipCode(''));
  };

  const handleOnItemClick = (productId: string, selected: boolean, isCheckbox: boolean) => {
    if (selected === false) {
      hasSelection.current = true;
      reduxDispatch(selectAddonService(productId));
    } else {
      if (isCheckbox) {
        // Only checkboxes can be deselected
        reduxDispatch(deselectAddonService(productId));
      }
    }
  };

  const getResults = () => {
    if (!isValidZipcode(getFormattedZipCode(zipCode)) || loadingStatus === LoadingStatus.Fetching) {
      return NO_ELEMENT;
    }
    if (validInstallations.length <= 0) {
      return <p className={styles.noResultText}>{rm('product_installation_error_not_in_area')}</p>;
    }

    const ppiFileName = getPPIFileName(addonServices);
    const ppiFilePath = getPPIFilePath(addonServices);

    return (
      <div className={styles.resultsContainer}>
        {showItemSection({
          addonServices: addonServices,
          onClickCallback: handleOnItemClick,
          validInstallations: validInstallations
        })}
        <a className={styles.ppiFile} href={ppiFilePath} target="_blank" rel="noreferrer">
          <IconPdf size="small" />
          <span>{ppiFileName}</span>
        </a>
      </div>
    );
  };

  return (
    <div className={styles.installationContainer}>
      <Header
        disabled={false}
        title={rm('installation_help')}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
      />
      {isOpen && (
        <>
          <ZipCodeInput
            title={rm('product_polarpump_installation_zipcode_header')}
            description={rm('product_polarpump_body')}
            zipCode={zipCode}
            onChange={setZipCode}
            loadingStatus={loadingStatus}
            inputClassName={styles.zipInput}
            descriptionClassName={styles.zipDescription}
            onClear={clearZipCode}
          />
          {getResults()}
        </>
      )}
    </div>
  );
};
